import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Package, FilterIcon, Eye, Edit, Trash2, CheckCircle2 } from 'lucide-react';
import AppliedFilters from '@/components/AppliedFilters';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import DataTable from '@/components/DataTable';
import Pagination from '@/components/Pagination';
import { useToast } from '@/components/ui/use-toast';
import { recoleccionesColumns, processRecoleccionData } from '../utils/recoleccionesColumns';
import { printBluetoothReceipt } from '@/utils/bluetoothPrinter';
import { getPrendasRecoleccion } from '../services/deposito.services';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import FilterDrawer from '@/components/FilterDrawer';
import ExportExcelButton from '@/components/ExportExcelButton';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import ModalVerificarRecoleccion from './ModalVerificarRecoleccion';
import ModalDetalleRecoleccion from './ModalDetalleRecoleccion';

const ITEMS_PER_PAGE = 10;
const DEFAULT_FILTERS = { estado: 'recibido' };

const SECTION_ICON_CLASS = 'h-8 w-8 text-primary';

const TablaRecolecciones = ({ recolecciones, onEdit, onDelete, onView, onVerificar, onPrint, idPlanta, onFiltersChange, loading: externalLoading }) => {
  const { toast } = useToast();
  const [displayedData, setDisplayedData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [filtersInitialized, setFiltersInitialized] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);

  // Sincronizar filtros iniciales con el padre
  useEffect(() => {
    if (!filtersInitialized && onFiltersChange) {
      onFiltersChange(DEFAULT_FILTERS);
      setFiltersInitialized(true);
    }
  }, [filtersInitialized, onFiltersChange]);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [isDetalleDialogOpen, setIsDetalleDialogOpen] = useState(false);
  const [itemToView, setItemToView] = useState(null);
  const [isVerificarDialogOpen, setIsVerificarDialogOpen] = useState(false);
  const [itemToVerificar, setItemToVerificar] = useState(null);

  // Procesar datos para mostrar en la tabla
  useEffect(() => {
    setIsLoading(true);
    try {
      // Calcular paginación local
      const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
      const endIndex = startIndex + ITEMS_PER_PAGE;
      const paginatedData = recolecciones.slice(startIndex, endIndex);

      // Procesar datos para la tabla - mantener referencia original
      const processedData = processRecoleccionData(paginatedData).map((processed, index) => ({
        ...processed,
        _originalData: paginatedData[index] // Guardar datos originales completos
      }));

      setDisplayedData(processedData);
      setTotalItems(recolecciones.length);
    } catch (error) {
      toast({ 
        title: 'Error', 
        description: 'Error al procesar los datos de recolecciones.', 
        variant: 'destructive' 
      });
    } finally {
      setIsLoading(false);
    }
  }, [recolecciones, currentPage, toast]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleDeleteConfirmation = (item) => {
    setItemToDelete(item);
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteItem = async () => {
    try {
      if (itemToDelete && onDelete) {
        await onDelete(itemToDelete);
        toast({ 
          title: "Recolección eliminada", 
          description: "La recolección fue eliminada correctamente." 
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || "Ocurrió un problema al eliminar la recolección.",
        variant: "destructive",
      });
    } finally {
      setIsDeleteDialogOpen(false);
      setItemToDelete(null);
    }
  };

  const handleViewItem = (item) => {
    // Usar datos originales si están disponibles
    const fullItem = item._originalData || recolecciones.find(r => {
      const rId = r._id || r.id;
      return rId === item.id || rId === item._id;
    });
    setItemToView(fullItem);
    setIsDetalleDialogOpen(true);
  };

  const handleVerificarItem = (item) => {
    // Buscar por _id o id
    const fullItem = recolecciones.find(r => 
      (r._id && r._id === item.id) || 
      (r.id && r.id === item.id) ||
      (r._id && r._id === item._id) ||
      (r.id && r.id === item._id)
    );
    
    console.log('Item recibido:', item);
    console.log('Full item encontrado:', fullItem);
    
    setItemToVerificar(fullItem);
    setIsVerificarDialogOpen(true);
  };

  const handleVerificarRecoleccion = async (recoleccionActualizada) => {
    try {
      if (onVerificar) {
        await onVerificar(recoleccionActualizada);
        setIsVerificarDialogOpen(false);
        setItemToVerificar(null);
      }
    } catch (error) {
      console.error('Error verificando recolección:', error);
      throw error;
    }
  };

  const getRecoleccionConPrendas = async (recoleccionData) => {
    if (!recoleccionData) {
      throw new Error('No se encontró la información de la recolección.');
    }

    if (recoleccionData.prendas && recoleccionData.prendas.length > 0) {
      return recoleccionData;
    }

    const id = recoleccionData._id || recoleccionData.id;

    if (!id) {
      throw new Error('La recolección no posee un identificador válido.');
    }

    const response = await getPrendasRecoleccion(id);
    const prendas = response?.data || [];

    if (!prendas.length) {
      throw new Error('No se encontraron prendas asociadas a la recolección.');
    }

    return {
      ...recoleccionData,
      prendas
    };
  };

  const handleAction = (actionType, row) => {
    // Usar datos originales si están disponibles, sino buscar en recolecciones
    const getFullItem = () => {
      if (row._originalData) {
        return row._originalData;
      }
      return recolecciones.find(r => {
        const rId = r._id || r.id;
        return rId === row.id || rId === row._id;
      });
    };

    const fullItem = getFullItem();

    if (actionType === 'edit' && onEdit) {
      onEdit(fullItem);
    } else if (actionType === 'delete') {
      handleDeleteConfirmation(row);
    } else if (actionType === 'view') {
      handleViewItem(row);
    } else if (actionType === 'verificar') {
      handleVerificarItem(row);
    } else if (actionType === 'print' && onPrint) {
      onPrint(fullItem || row);
    } else if (actionType === 'preview') {
      handleViewItem(row);
    } else if (actionType === 'bluetooth') {
      handleBluetoothPrint(fullItem || row);
    }
  };

  const handleBluetoothPrint = async (recoleccionData) => {
    try {
      const detalle = await getRecoleccionConPrendas(recoleccionData);

      toast({
        title: 'Conectando a la impresora',
        description: 'Confirma la conexión Bluetooth en tu navegador.',
      });

      await printBluetoothReceipt(detalle);

      toast({
        title: 'Impresión enviada',
        description: 'El recibo se está imprimiendo en la PT-210.',
      });
    } catch (error) {
      console.error('Error imprimiendo por Bluetooth:', error);
      toast({
        title: 'Error de impresión',
        description: error.message || 'No se pudo imprimir por Bluetooth.',
        variant: 'destructive'
      });
    }
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    setCurrentPage(1); // Reset to first page when filters change
    setIsDrawerOpen(false);
    
    // Notificar al componente padre para que haga la búsqueda
    if (onFiltersChange) {
      onFiltersChange(newFilters);
    }
  };

  const handleRemoveFilter = (key) => {
    const newFilters = { ...filters };
    delete newFilters[key];
    setFilters(newFilters);
    setCurrentPage(1);
    
    // Notificar al componente padre para que haga la búsqueda
    if (onFiltersChange) {
      onFiltersChange(newFilters);
    }
  };

  const getEstadoBadge = (estado) => {
    switch (estado) {
      case 'recibido':
        return <Badge variant="outline" className="text-blue-600 border-blue-600">Recibido</Badge>;
      case 'pendiente_verificacion':
        return <Badge variant="outline" className="text-yellow-600 border-yellow-600">Pendiente</Badge>;
      case 'verificado':
        return <Badge variant="outline" className="text-green-600 border-green-600">Verificado</Badge>;
      case 'rechazado':
        return <Badge variant="outline" className="text-red-600 border-red-600">Rechazado</Badge>;
      case 'entregado':
        return <Badge variant="outline" className="text-purple-600 border-purple-600">Entregado</Badge>;
      default:
        return <Badge variant="outline">Desconocido</Badge>;
    }
  };

  const EXCEL_COLUMNS = [
    { key: 'consecutivo',      header: 'Consecutivo' },
    { key: 'estado',           header: 'Estado' },
    { key: 'empleadoNombre',   header: 'Empleado' },
    { key: 'empleadoCedula',   header: 'Cédula' },
    { key: 'empleadoCelular',  header: 'Celular' },
    { key: 'cantidadPrendas',  header: 'Total Prendas' },
    { key: 'fechaCreacion',    header: 'Fecha Creación' },
  ];

  const exportRecolecciones = async () => {
    return recolecciones.map(r => ({
      consecutivo:     r.consecutivo || 'N/A',
      estado:          r.estado || '',
      empleadoNombre:  `${r.empleado?.nombre || ''} ${r.empleado?.apellido || ''}`.trim(),
      empleadoCedula:  r.empleado?.cedula || '',
      empleadoCelular: r.empleado?.celular || '',
      cantidadPrendas: r.cantidad_prendas || 0,
      fechaCreacion:   r.createdAt ? new Date(r.createdAt).toLocaleString('es-CO') : '',
    }));
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Card>
        <CardHeader className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
          <div className="w-full">
            <div className="flex items-center space-x-3 mb-2">
              <Package className={SECTION_ICON_CLASS} />
              <CardTitle className="text-2xl">Tabla de Recepción</CardTitle>
            </div>
          </div>

          <div className="w-full flex flex-col gap-3">
            <div className="flex flex-wrap gap-2 justify-end">
              <ExportExcelButton
                fileName="Recolecciones"
                columns={EXCEL_COLUMNS}
                service={exportRecolecciones}
              />
              <Button 
                onClick={() => setIsDrawerOpen(true)} 
                className="flex items-center font-semibold bg-black hover:bg-black/20"
              >
                <FilterIcon className="mr-2 h-4 w-4" />
                Filtros
              </Button>
            </div>
          </div>
        </CardHeader>

        <CardContent>
          <div className="w-full">
            <AppliedFilters 
              filters={filters} 
              fields={[
                { 
                  id: 'estado', 
                  label: 'Estado', 
                  type: 'select', 
                  options: [
                    { value: 'recibido', label: 'Recibido' },
                    { value: 'pendiente_verificacion', label: 'Pendiente Verificación' },
                    { value: 'verificado', label: 'Verificado' },
                    { value: 'rechazado', label: 'Rechazado' },
                    { value: 'entregado', label: 'Entregado' }
                  ]
                },
                { id: 'consecutivo', label: 'Consecutivo', type: 'text' },
                { id: 'empleado.nombre', label: 'Empleado', type: 'text' },
                { id: 'empleado.cedula', label: 'Cédula', type: 'text' },
                { id: 'casillero', label: 'Casillero', type: 'text' }
              ]}
              onRemoveFilter={handleRemoveFilter}
            />
          </div>

          <DataTable
            data={displayedData}
            columns={recoleccionesColumns}
            onAction={handleAction}
            isLoading={externalLoading || isLoading}
            showCreateButton={false}
          />

          <Pagination
            currentPage={currentPage}
            totalItems={totalItems}
            itemsPerPage={ITEMS_PER_PAGE}
            onPageChange={handlePageChange}
          />

          <FilterDrawer
            isOpen={isDrawerOpen}
            onClose={() => setIsDrawerOpen(false)}
            fields={[
              {
                id: 'estado',
                label: 'Estado',
                type: 'select',
                options: [
                  { value: 'recibido', label: 'Recibido' },
                  { value: 'pendiente_verificacion', label: 'Pendiente Verificación' },
                  { value: 'verificado', label: 'Verificado' },
                  { value: 'rechazado', label: 'Rechazado' },
                  { value: 'entregado', label: 'Entregado' }
                ]
              },
              { id: 'empleado.nombre', label: 'Empleado', type: 'text' },
              { id: 'empleado.cedula', label: 'Cédula', type: 'text' },
              { id: 'casillero', label: 'Casillero', type: 'text' },
              { id: 'consecutivo', label: 'Consecutivo', type: 'text' }
            ]}
            initialFilters={filters}
            onChange={handleFilterChange}
            onApply={handleFilterChange}
          />
        </CardContent>
      </Card>

      {/* Modal de detalles de recolección */}
      <ModalDetalleRecoleccion
        isOpen={isDetalleDialogOpen}
        onClose={() => {
          setIsDetalleDialogOpen(false);
          setItemToView(null);
        }}
        recoleccion={itemToView}
      />

      {/* Modal de verificación  */}
      <ModalVerificarRecoleccion
        isOpen={isVerificarDialogOpen}
        onClose={() => {
          setIsVerificarDialogOpen(false);
          setItemToVerificar(null);
        }}
        recoleccion={itemToVerificar}
        onVerificar={handleVerificarRecoleccion}
      />

      {/* Dialog de confirmación de eliminación */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Estás seguro de eliminar esta recolección?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. Esto eliminará permanentemente la recolección
              del empleado "{itemToDelete?.['empleado.nombre']}" de tus registros.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setItemToDelete(null)}>Cancelar</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteItem} 
              className="bg-destructive hover:bg-destructive/90"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </motion.div>
  );
};

export default TablaRecolecciones;
